import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { usePermissions } from '../lib/usePermissions';
import { usersApi, storesApi, brandsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Users, UserPlus, Pencil, Power, PowerOff, Save, X, Mail, Briefcase, Shield, Trash2, IdCard } from 'lucide-react';
import { toast } from 'sonner';
import StoreMultiSelector from '../components/StoreMultiSelector';

const ROLES = [
  { value: 'operations_manager', label: 'Operations Manager' },
  { value: 'hr_manager', label: 'HR Manager' },
  { value: 'area_manager', label: 'Area Manager' },
  { value: 'store_manager', label: 'Store Manager' },
  { value: 'store_user', label: 'Staff' },
];

function statusOf(u) {
  if (u.status) return u.status;
  return u.active === false ? 'suspended' : 'active';
}

function StatusBadge({ status }) {
  const map = {
    invited: { text: 'Invitato', cls: 'text-amber-700 bg-amber-50 border-amber-300' },
    active: { text: 'Attivo', cls: 'text-emerald-700 bg-emerald-50 border-emerald-300' },
    suspended: { text: 'Sospeso', cls: 'text-red-700 bg-red-50 border-red-300' },
  };
  const it = map[status] || map.active;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border ${it.cls}`} data-testid={`status-${status}`}>
      {it.text}
    </span>
  );
}

function fmtLastLogin(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'adesso';
  if (mins < 60) return `${mins} min fa`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}g fa`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editStores, setEditStores] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null); // user object pending suspension/activation
  const [roleTarget, setRoleTarget] = useState(null); // user object pending role change
  const [nameTarget, setNameTarget] = useState(null); // user object pending name edit
  const [deleteTarget, setDeleteTarget] = useState(null); // user object pending hard delete
  const [resending, setResending] = useState(null);  // user_id currently being re-invited

  const resendInvite = async (u) => {
    setResending(u.user_id);
    try {
      const r = await usersApi.resendInvite(u.user_id);
      if (r.data?.status === 'sent') {
        toast.success(`Email reinviata a ${u.email}`);
      } else {
        toast.error(`Invio fallito: ${r.data?.error || 'errore sconosciuto'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il reinvio');
    } finally {
      setResending(null);
    }
  };

  const isOps = currentUser?.role === 'operations_manager';
  const isAM = currentUser?.role === 'area_manager';
  const { has } = usePermissions();
  // Gating dinamico (refactor 2026-05-29): `users.invite` flag dinamico.
  const canInvite = has('users.invite');

  const reload = useCallback(async () => {
    const [uRes, sRes, bRes] = await Promise.all([
      usersApi.list(),
      storesApi.list({ all: true }),
      brandsApi.list(),
    ]);
    setUsers(uRes.data); setStores(sRes.data); setBrands(bRes.data || []);
  }, []);

  useEffect(() => {
    reload().catch(console.error).finally(() => setLoading(false));
  }, [reload]);

  const storeMap = useMemo(() => Object.fromEntries(stores.map(s => [s.store_id, s])), [stores]);

  const ROLE_LEVEL = { operations_manager: 4, hr_manager: 3, area_manager: 2, store_manager: 1, store_user: 0 };

  const canEditUser = (u) => {
    if (u.user_id === currentUser?.user_id) return false;
    if (isOps) {
      // OPS può modificare AM e Store User, ma NON altri OPS.
      return u.role !== 'operations_manager';
    }
    // Gerarchia: si possono modificare solo utenti di livello inferiore al proprio.
    return ROLE_LEVEL[u.role] < ROLE_LEVEL[currentUser?.role];
  };

  const startEditStores = (u) => {
    setEditing(u.user_id);
    setEditStores(u.store_ids || []);
  };

  const saveStores = async (u) => {
    await usersApi.update(u.user_id, { store_ids: editStores });
    setEditing(null);
    await reload();
  };

  const confirmStatus = async () => {
    if (!statusTarget) return;
    const next = statusOf(statusTarget) === 'suspended' ? 'active' : 'suspended';
    await usersApi.update(statusTarget.user_id, { status: next });
    setStatusTarget(null);
    await reload();
  };

  const confirmRoleChange = async (newRole) => {
    if (!roleTarget) return;
    try {
      // Se il nuovo ruolo è OPS o HR Manager, l'utente vede automaticamente
      // tutti i negozi → svuotiamo store_ids per evitare assegnazioni stantie.
      const payload = { role: newRole };
      if (newRole === 'operations_manager' || newRole === 'hr_manager') {
        payload.store_ids = [];
      }
      await usersApi.update(roleTarget.user_id, payload);
      toast.success(`Ruolo aggiornato per ${roleTarget.name || roleTarget.email}`);
      setRoleTarget(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento ruolo');
    }
  };

  const confirmNameChange = async (newName) => {
    if (!nameTarget) return;
    try {
      await usersApi.update(nameTarget.user_id, { name: newName });
      toast.success(`Nome aggiornato per ${newName}`);
      setNameTarget(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento nome');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await usersApi.delete(deleteTarget.user_id);
      toast.success(`Utente ${deleteTarget.name || deleteTarget.email} eliminato`);
      setDeleteTarget(null);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione utente');
    }
  };

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>;

  // Page-level RBAC gate (2026-05-25): UsersPage è OPS+AM-only.
  // Backend già respinge GET /api/users dai Store User con 403, ma fino
  // ad oggi un SU poteva approdare qui via URL diretto e vedere errori
  // di permesso o schermata vuota. Redirect immediato + UX pulita.
  // Guard DOPO tutti gli hook (regola React rules-of-hooks).
  if (currentUser && !isOps && !isAM) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="users-page">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Utenti e Permessi</h1>
          <p className="text-slate-500 mt-1">
            {isAM ? 'Invita Area Manager, Store Manager e Staff; gestisci gli accessi e le assegnazioni risorse tra i negozi.' : 'Invita Operations Manager, HR Manager, Area Manager, Store Manager e Staff; gestisci ruoli, permessi e assegnazioni.'}
          </p>
        </div>
        {canInvite && (
          <Button
            onClick={() => setInviteOpen(true)}
            className="rounded-none bg-black text-white hover:bg-slate-900"
            data-testid="invite-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" /> Invita utente
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200">
        {users.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun utente</p>
          </div>
        ) : (
          <table className="w-full data-table">
            <thead>
              <tr>
                <th>Utente</th>
                <th>Email</th>
                <th>Ruolo</th>
                <th>Negozi assegnati</th>
                <th>Stato</th>
                <th>Ultimo accesso</th>
                <th>Invitato da</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const status = statusOf(u);
                const editable = canEditUser(u);
                const isEditingThis = editing === u.user_id;
                return (
                  <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                    <td data-label="Utente">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-slate-200 flex items-center justify-center">
                            <span className="text-xs font-bold text-slate-500">{u.name?.[0]?.toUpperCase() || '?'}</span>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <span className="font-medium block truncate">{u.name || '—'}</span>
                          {/* Badge "Anagrafica staff collegata" (fix 2026-05-29):
                              indica visivamente quando l'account utente è
                              linkato ad una scheda dipendente. Cliccando si
                              naviga alla scheda. */}
                          {u.linked_staff && (
                            <Link
                              to={`/staff/${u.linked_staff.staff_id}`}
                              className="inline-flex items-center gap-1 text-[10px] mt-0.5 text-violet-700 hover:text-violet-900 hover:underline underline-offset-2"
                              data-testid={`user-linked-staff-${u.user_id}`}
                              title="Scheda dipendente collegata — clicca per aprire"
                            >
                              <Briefcase className="w-3 h-3" />
                              Anagrafica: {u.linked_staff.name}
                            </Link>
                          )}
                        </div>
                      </div>
                    </td>
                    <td data-label="Email" className="text-slate-500 text-xs">{u.email}</td>
                    <td data-label="Ruolo">
                      <span className="text-xs uppercase tracking-wider font-medium px-2 py-0.5 border bg-slate-50">
                        {ROLES.find(r => r.value === u.role)?.label || u.role}
                      </span>
                    </td>
                    <td data-label="Negozi" className="min-w-[280px]">
                      {isEditingThis ? (
                        <div className="space-y-1.5">
                          {u.role === 'area_manager' && (
                            <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 px-2 py-1" data-testid={`am-stores-hint-${u.user_id}`}>
                              Le assegnazioni di un Area Manager si sincronizzano automaticamente con la sua anagrafica (Impostazioni → Mappatura Negozi).
                            </p>
                          )}
                          <StoreMultiSelector
                            stores={stores}
                            brands={brands}
                            value={editStores}
                            onChange={setEditStores}
                            testIdPrefix={`edit-store-toggle-user-${u.user_id}`}
                            maxHeight="max-h-48"
                            groupByBrand
                          />
                          <div className="flex gap-2 mt-1">
                            <Button size="sm" className="rounded-none h-7 text-xs bg-black text-white hover:bg-slate-900" onClick={() => saveStores(u)} data-testid={`save-stores-${u.user_id}`}>
                              <Save className="w-3 h-3 mr-1" /> Salva
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-none h-7 text-xs" onClick={() => setEditing(null)}>
                              <X className="w-3 h-3 mr-1" /> Annulla
                            </Button>
                          </div>
                        </div>
                      ) : u.store_ids?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {u.store_ids.map(sid => (
                            <span key={sid} className="text-xs px-1.5 py-0.5 bg-slate-100 border border-slate-200">
                              {storeMap[sid]?.display_name || sid}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">{u.role === 'operations_manager' || u.role === 'hr_manager' ? 'Tutti' : 'Nessuno'}</span>
                      )}
                    </td>
                    <td data-label="Stato"><StatusBadge status={status} /></td>
                    <td data-label="Ultimo accesso" className="text-xs text-slate-500" data-testid={`last-login-${u.user_id}`}>
                      {fmtLastLogin(u.last_login_at) || <span className="text-slate-300">Mai</span>}
                    </td>
                    <td data-label="Invitato da" className="text-xs text-slate-500">
                      {u.invited_by_name || (u.invited_by ? '—' : <span className="text-slate-300">—</span>)}
                    </td>
                    <td>
                      {editable && !isEditingThis && u.user_id !== currentUser?.user_id && (
                        <div className="flex items-center gap-1">
                          {status === 'invited' && (
                            <button
                              onClick={() => resendInvite(u)}
                              disabled={resending === u.user_id}
                              title="Reinvia email di invito"
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-40"
                              data-testid={`resend-invite-${u.user_id}`}
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => startEditStores(u)}
                            title="Modifica negozi assegnati"
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            data-testid={`edit-stores-${u.user_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setNameTarget(u)}
                            title="Modifica nome utente"
                            className="p-1.5 text-slate-400 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`edit-name-${u.user_id}`}
                          >
                            <IdCard className="w-3.5 h-3.5" />
                          </button>
                          {isOps && (
                            <button
                              onClick={() => setRoleTarget(u)}
                              title="Cambia ruolo utente"
                              className="p-1.5 text-slate-400 hover:text-violet-700 hover:bg-violet-50"
                              data-testid={`change-role-${u.user_id}`}
                            >
                              <Shield className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            onClick={() => setStatusTarget(u)}
                            title={status === 'suspended' ? 'Riattiva utente' : 'Sospendi utente'}
                            className={`p-1.5 hover:bg-slate-100 ${status === 'suspended' ? 'text-emerald-600' : 'text-slate-400 hover:text-red-600'}`}
                            data-testid={`toggle-status-${u.user_id}`}
                          >
                            {status === 'suspended' ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                          </button>
                          {isOps && status === 'suspended' && (
                            <button
                              onClick={() => setDeleteTarget(u)}
                              title="Elimina utente definitivamente"
                              className="p-1.5 text-slate-400 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <InviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        stores={stores}
        brands={brands}
        currentRole={currentUser?.role}
        onInvited={async () => { setInviteOpen(false); await reload(); }}
      />

      <AlertDialog open={!!statusTarget} onOpenChange={(o) => { if (!o) setStatusTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="status-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget && (statusOf(statusTarget) === 'suspended' ? 'Riattivare ' : 'Sospendere ')}
              {statusTarget?.name || statusTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTarget && statusOf(statusTarget) === 'suspended'
                ? 'L\'utente potrà di nuovo accedere alla piattaforma con i negozi attualmente assegnati.'
                : 'L\'utente non potrà più accedere alla piattaforma. Le sue assegnazioni verranno preservate per un\'eventuale riattivazione.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="status-confirm-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmStatus}
              className={`rounded-none ${statusTarget && statusOf(statusTarget) === 'suspended' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}`}
              data-testid="status-confirm-apply"
            >
              {statusTarget && statusOf(statusTarget) === 'suspended' ? 'Riattiva' : 'Sospendi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ChangeRoleDialog
        target={roleTarget}
        onClose={() => setRoleTarget(null)}
        onApply={confirmRoleChange}
      />

      <EditNameDialog
        target={nameTarget}
        onClose={() => setNameTarget(null)}
        onApply={confirmNameChange}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none" data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminare definitivamente {deleteTarget?.name || deleteTarget?.email}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;account verrà rimosso dalla piattaforma in modo permanente. Le sessioni attive verranno revocate. Eventuali schede dipendente collegate resteranno integre (verrà solo rimosso il collegamento all&apos;account).
              <br /><br />
              <strong>Storico ticket, note, audit log e azioni passate vengono preservati.</strong>
              <br /><br />
              Questa operazione è <strong>irreversibile</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="delete-user-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-none bg-red-600 hover:bg-red-700"
              data-testid="delete-user-apply"
            >
              Elimina definitivamente
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditNameDialog({ target, onClose, onApply }) {
  const [name, setName] = useState(target?.name || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) setName(target.name || '');
  }, [target]);

  const trimmed = name.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 120 && trimmed !== (target?.name || '');

  const handleSubmit = async () => {
    if (!valid) { onClose(); return; }
    setSubmitting(true);
    try { await onApply(trimmed); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="edit-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Modifica nome · {target?.email}</DialogTitle>
          <DialogDescription>
            Aggiorna il nome visualizzato dell&apos;utente. L&apos;email non cambia.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Nome completo *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Es. Mario Rossi"
            className="rounded-none"
            maxLength={120}
            autoFocus
            data-testid="edit-name-input"
            onKeyDown={(e) => { if (e.key === 'Enter' && valid && !submitting) handleSubmit(); }}
          />
          <p className="text-xs text-slate-500">{trimmed.length}/120 caratteri</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={submitting} data-testid="edit-name-cancel">Annulla</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !valid}
            className="rounded-none bg-black text-white hover:bg-slate-900 disabled:opacity-40"
            data-testid="edit-name-apply"
          >
            {submitting ? 'Salvo…' : 'Salva nome'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRoleDialog({ target, onClose, onApply }) {
  const [newRole, setNewRole] = useState(target?.role || 'store_user');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (target) setNewRole(target.role);
  }, [target]);

  const handleSubmit = async () => {
    if (!newRole || newRole === target?.role) {
      onClose();
      return;
    }
    setSubmitting(true);
    try { await onApply(newRole); }
    finally { setSubmitting(false); }
  };

  const willClearStores = (newRole === 'operations_manager' || newRole === 'hr_manager') && (target?.store_ids?.length || 0) > 0;

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none sm:max-w-md" data-testid="change-role-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Cambia ruolo · {target?.name || target?.email}</DialogTitle>
          <DialogDescription>
            Il ruolo determina permessi e visibilità su negozi, dati e moduli. Cambia con cautela.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-xs text-slate-500">
            Ruolo attuale: <span className="font-semibold uppercase tracking-wide text-slate-900">{ROLES.find(r => r.value === target?.role)?.label || target?.role}</span>
          </div>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="rounded-none" data-testid="change-role-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLES.map(r => (
                <SelectItem
                  key={r.value}
                  value={r.value}
                  disabled={r.value === target?.role}
                  data-testid={`change-role-opt-${r.value}`}
                >
                  {r.label}{r.value === target?.role ? ' · attuale' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {willClearStores && (
            <div className="text-xs bg-amber-50 border border-amber-200 p-2 text-amber-800" data-testid="change-role-warning-stores">
              ⚠️ Il nuovo ruolo vede tutti i negozi automaticamente. I {target?.store_ids?.length || 0} negozi attualmente assegnati verranno rimossi.
            </div>
          )}
          {newRole === target?.role && (
            <div className="text-xs text-slate-500">Seleziona un ruolo diverso per applicare.</div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={submitting} data-testid="change-role-cancel">Annulla</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !newRole || newRole === target?.role}
            className="rounded-none bg-black text-white hover:bg-slate-900 disabled:opacity-40"
            data-testid="change-role-apply"
          >
            {submitting ? 'Salvo…' : 'Applica nuovo ruolo'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ open, onClose, stores, brands = [], currentRole, onInvited }) {
  const isOps = currentRole === 'operations_manager';
  // OPS può invitare OPS/HR/AM/SM/Staff. AM può invitare solo ruoli inferiori (SM/Staff).
  const allowedRoles = isOps
    ? [
        { value: 'operations_manager', label: 'Operations Manager' },
        { value: 'hr_manager', label: 'HR Manager' },
        { value: 'area_manager', label: 'Area Manager' },
        { value: 'store_manager', label: 'Store Manager' },
        { value: 'store_user', label: 'Staff' },
      ]
    : [
        { value: 'store_manager', label: 'Store Manager' },
        { value: 'store_user', label: 'Staff' },
      ];
  const [role, setRole] = useState('store_user');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [picked, setPicked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setRole('store_user'); setEmail(''); setName(''); setPicked([]); setError(''); }
  }, [open]);

  // Reset selezione quando si passa da/verso OPS (che non usa store).
  useEffect(() => {
    if (role === 'operations_manager') setPicked([]);
  }, [role]);

  // Negozi obbligatori solo per Staff / Store Manager.
  // HR Manager e Area Manager possono ricevere un set di negozi opzionale
  // (lo scoping aggiuntivo viene applicato dalla RBAC `get_user_allowed_store_ids`).
  // OPS vede sempre tutto e non ha bisogno di store assegnati.
  const needsStoresRequired = role === 'store_user' || role === 'store_manager';
  const canAssignStores = role !== 'operations_manager';
  const valid = /\S+@\S+\.\S+/.test(email) && (!needsStoresRequired || picked.length > 0);

  const submit = async () => {
    setSubmitting(true); setError('');
    try {
      const r = await usersApi.invite({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        store_ids: canAssignStores ? picked : [],
      });
      if (r.data?.email_status === 'failed') {
        toast.warning(`Utente creato ma email non recapitata: ${r.data?.email_error || 'errore Resend'}. Usa "Reinvia invito".`);
      } else if (r.data?.email_status === 'sent') {
        toast.success(`Invito inviato a ${r.data.email}`);
      }
      await onInvited();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Errore durante l\'invito');
    } finally { setSubmitting(false); }
  };

  const titleByRole = {
    operations_manager: 'Invita Operations Manager',
    hr_manager: 'Invita HR Manager',
    area_manager: 'Invita Area Manager',
    store_manager: 'Invita Store Manager',
    store_user: 'Invita Staff',
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{titleByRole[role] || 'Invita utente'}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {role === 'store_user'
              ? 'Lo Staff accederà con la sua email Google. Al primo login troverà già i negozi assegnati e potrà visualizzarne i dati.'
              : role === 'store_manager'
                ? 'Lo Store Manager accederà con la sua email Google. Gestirà i negozi assegnati con poteri completi (come AM ma scoped).'
                : role === 'hr_manager'
                  ? 'L\'HR Manager accederà con la sua email Google. Gestirà tutto lo Staff (anagrafica, documenti, achievements) ma NON Budget/Costi/Settings/Dashboard.'
                  : role === 'area_manager'
                    ? 'L\'Area Manager accederà con la sua email Google. I negozi selezionati qui sotto saranno effettivi da subito (sincronizzati con la sua anagrafica).'
                    : 'L\'Operations Manager avrà accesso completo a tutta la piattaforma al primo login.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Ruolo *</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none" data-testid="invite-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowedRoles.map(r => (
                  <SelectItem key={r.value} value={r.value} data-testid={`invite-role-opt-${r.value}`}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Email Google *</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome.cognome@gmail.com"
                className="rounded-none"
                data-testid="invite-email"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">Nome (opzionale)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Es. Mario Rossi"
                className="rounded-none"
                data-testid="invite-name"
              />
            </div>
          </div>
          {canAssignStores && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 block mb-1">
                Negozi assegnati {needsStoresRequired ? '*' : '(opzionale)'}
              </label>
              <StoreMultiSelector
                stores={stores}
                brands={brands}
                value={picked}
                onChange={setPicked}
                testIdPrefix="invite-store"
                maxHeight="max-h-60"
                groupByBrand
                helperText={
                  needsStoresRequired
                    ? 'Seleziona almeno un negozio. L\'utente vedrà solo i dati dei negozi assegnati.'
                    : role === 'hr_manager'
                      ? 'Riferimento organizzativo (l\'HR Manager mantiene comunque visibilità completa sullo Staff). Utile per audit e reportistica.'
                      : role === 'area_manager'
                        ? 'I negozi selezionati diventano la giurisdizione effettiva dell\'AM (sincronizzati automaticamente con la sua anagrafica).'
                        : ''
                }
              />
            </div>
          )}
          {error && <p className="text-xs text-red-600" data-testid="invite-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" className="rounded-none" onClick={onClose} disabled={submitting}>Annulla</Button>
            <Button
              onClick={submit}
              disabled={!valid || submitting}
              className="rounded-none bg-black text-white hover:bg-slate-900 disabled:opacity-40"
              data-testid="invite-submit"
            >
              {submitting ? 'Invio in corso...' : 'Invita'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
